import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  TablePagination,
  useTheme,
  Drawer,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { MoreVertical, Eye, Mail, X, Briefcase, Download, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePayments } from '../../hooks/payment/usePayments';
import { format } from 'date-fns';
import type { InvoiceStatus, Invoice } from '../../types/payment';
import { RefundDialog } from './RefundDialog';

export const InvoicesList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    invoices,
    invoicesLoading,
    invoicesError,
    refreshInvoices,
    resendInvoice,
    downloadInvoice,
    invoicesTotalCount,
  } = usePayments();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Refund dialog state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState<Invoice | null>(null);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInvoice, setMenuInvoice] = useState<Invoice | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuInvoice(invoice);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuInvoice(null);
  };

  const handleViewInvoice = () => {
    if (menuInvoice) {
      handleViewDetails(menuInvoice);
    }
    handleMenuClose();
  };

  const handleViewJob = () => {
    if (menuInvoice?.bookingId && typeof menuInvoice.bookingId === 'number') {
      navigate(`/dashboard/bookings/${menuInvoice.bookingId}`);
    }
    handleMenuClose();
  };

  const handleResendInvoice = async () => {
    if (menuInvoice) {
      await resendInvoice(menuInvoice);
    }
    handleMenuClose();
  };

  const handleDownloadInvoice = async () => {
    if (menuInvoice) {
      await downloadInvoice(menuInvoice);
    }
    handleMenuClose();
  };

  const handleRefundInvoice = () => {
    if (menuInvoice) {
      setRefundInvoice(menuInvoice);
      setRefundDialogOpen(true);
    }
    handleMenuClose();
  };

  useEffect(() => {
    refreshInvoices({ page: page + 1, pageSize });
  }, [page, pageSize, refreshInvoices]);

  const handleViewDetails = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedInvoice(null);
  };

  const getStatusColor = (
    status: InvoiceStatus
  ): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'UNPAID':
        return 'warning';
      case 'PARTIAL_REFUNDED':
      case 'REFUNDED':
        return 'info';
      case 'FAILED':
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  // Show error if exists
  if (invoicesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="body2" gutterBottom fontWeight={600}>
            Failed to load invoices
          </Typography>
          <Typography variant="body2">{invoicesError}</Typography>
          <Button size="small" onClick={() => refreshInvoices()} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  if (invoicesLoading && invoices.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading invoices...
        </Typography>
      </Box>
    );
  }

  if (!invoicesLoading && invoices.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No invoices yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invoices will appear here when bookings with paid services are created.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Invoices
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>Invoice #</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Date</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Customer</TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                Amount
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map(invoice => (
              <TableRow key={invoice.id} hover>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{format(new Date(invoice.createdAt), 'MMM d, yyyy h:mm a')}</TableCell>
                <TableCell>
                  {invoice.customer
                    ? `${invoice.customer.firstName} ${invoice.customer.lastName || ''}`.trim()
                    : 'N/A'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(invoice.totalCents, invoice.currency)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    color={getStatusColor(invoice.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={e => handleMenuOpen(e, invoice)}>
                    <MoreVertical size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={invoicesTotalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={e => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50]}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewInvoice}>
          <ListItemIcon>
            <Eye size={18} />
          </ListItemIcon>
          <ListItemText>View Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewJob} disabled={!menuInvoice?.bookingId}>
          <ListItemIcon>
            <Briefcase size={18} />
          </ListItemIcon>
          <ListItemText>View Job</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadInvoice}>
          <ListItemIcon>
            <Download size={18} />
          </ListItemIcon>
          <ListItemText>Download Invoice</ListItemText>
        </MenuItem>
        {menuInvoice?.status === 'UNPAID' && (
          <MenuItem onClick={handleResendInvoice}>
            <ListItemIcon>
              <Mail size={18} />
            </ListItemIcon>
            <ListItemText>Resend Invoice</ListItemText>
          </MenuItem>
        )}
        {(menuInvoice?.status === 'PAID' || menuInvoice?.status === 'PARTIAL_REFUNDED') && (
          <MenuItem onClick={handleRefundInvoice}>
            <ListItemIcon>
              <RotateCcw size={18} />
            </ListItemIcon>
            <ListItemText>Refund</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Refund Dialog */}
      {refundInvoice && (
        <RefundDialog
          open={refundDialogOpen}
          onClose={() => {
            setRefundDialogOpen(false);
            setRefundInvoice(null);
          }}
          invoice={refundInvoice}
          onSuccess={() => refreshInvoices({ page: page + 1, pageSize })}
        />
      )}

      {/* Invoice Detail Drawer */}
      <Drawer
        anchor="right"
        open={detailDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            p: 0,
            py: { xs: 2, sm: 2 },
            px: { xs: 2, sm: 3 },
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Invoice Details</Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ flex: 1, overflowY: 'auto' }}>
            {selectedInvoice && (
              <Box>
                {/* Invoice Header */}
                <Box mb={3}>
                  <Typography variant="h4" gutterBottom>
                    {selectedInvoice.invoiceNumber}
                  </Typography>
                  <Chip
                    label={selectedInvoice.status}
                    color={getStatusColor(selectedInvoice.status)}
                    size="small"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Invoice Details Grid */}
                <Box display="flex" flexDirection="column" gap={2} mb={3}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Invoice Date
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(selectedInvoice.createdAt), 'MMMM d, yyyy')}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(selectedInvoice.dueDate), 'MMMM d, yyyy')}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body2">
                      {selectedInvoice.customer
                        ? `${selectedInvoice.customer.firstName} ${selectedInvoice.customer.lastName || ''}`.trim()
                        : 'N/A'}
                    </Typography>
                  </Box>
                  {selectedInvoice.paidAt && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Paid On
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        {format(new Date(selectedInvoice.paidAt), 'MMMM d, yyyy')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Line Items */}
                {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Line Items
                    </Typography>
                    {selectedInvoice.lineItems.map(item => (
                      <Box
                        key={item.id}
                        display="flex"
                        justifyContent="space-between"
                        py={1}
                        borderBottom="1px solid"
                        borderColor="divider"
                      >
                        <Box>
                          <Typography variant="body2">{item.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qty: {item.quantity} ×{' '}
                            {formatCurrency(item.unitPriceCents, selectedInvoice.currency)}
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {formatCurrency(item.totalCents, selectedInvoice.currency)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Totals */}
                <Box bgcolor="action.hover" p={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedInvoice.subtotalCents, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Tax
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedInvoice.taxCents, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Platform Fee
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedInvoice.platformFeeCents, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedInvoice.totalCents, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {selectedInvoice?.status === 'UNPAID' && (
              <Button
                variant="contained"
                startIcon={<Mail size={16} />}
                onClick={() => {
                  handleCloseDialog();
                  resendInvoice(selectedInvoice);
                }}
                disableElevation
              >
                Resend Invoice
              </Button>
            )}
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Box>
      </Drawer>
    </Box>
  );
};
